"use client"

import type React from "react"
import { useState, useEffect } from "react"
import type { AuthError } from "firebase/auth"
import { auth, db, loggedSetDoc } from "../lib/firebase"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { doc } from "firebase/firestore"
import { serverTimestamp } from "firebase/firestore"

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  context?: "normal" | "purchase"
}

export default function AuthModal({ isOpen, onClose, context = "normal" }: AuthModalProps) {
  const [isLogin, setIsLogin] = useState(true)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [newsletterOptIn, setNewsletterOptIn] = useState(false)
  const [verificationSent, setVerificationSent] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [resetEmailSent, setResetEmailSent] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setErrorMessage("")
    }
  }, [isOpen])

  const closeAuthModalAndClearError = () => {
    setErrorMessage("")
    onClose()
  }

  const handleForgotPassword = async () => {
    setErrorMessage("")
    if (!email) {
      setErrorMessage("Vänligen ange din e-postadress först.")
      return
    }

    try {
      const { sendPasswordResetEmail } = await import("firebase/auth")
      await sendPasswordResetEmail(auth, email)
      setResetEmailSent(true)
    } catch (error) {
      const authError = error as AuthError
      if (process.env.NODE_ENV === "development") {
        console.error("Password reset error:", authError.code, authError.message)
      }
      switch (authError.code) {
        case "auth/user-not-found":
          setErrorMessage("Ingen användare med denna e-postadress hittades.")
          break
        case "auth/invalid-email":
          setErrorMessage("Ogiltig e-postadress.")
          break
        default:
          setErrorMessage("Ett fel uppstod. Försök igen senare.")
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage("")
    try {
      if (isLogin) {
        const { signInWithEmailAndPassword } = await import("firebase/auth")
        await signInWithEmailAndPassword(auth, email, password)
        closeAuthModalAndClearError()
      } else {
        if (!acceptTerms) {
          setErrorMessage("Du måste acceptera villkoren för att fortsätta.")
          return
        }

        const [authModule] = await Promise.all([import("firebase/auth"), import("firebase/firestore")])

        const { createUserWithEmailAndPassword, sendEmailVerification } = authModule
        const userCredential = await createUserWithEmailAndPassword(auth, email, password)
        const user = userCredential.user

        // Create user document in Firestore
        const userDocRef = doc(db, "users", user.uid)
        await loggedSetDoc(userDocRef, {
          email: user.email,
          acceptTerms,
          subscribeNewsletter: newsletterOptIn,
          createdAt: serverTimestamp(),
          emailVerified: false,
        })

        await sendEmailVerification(user)
        setVerificationSent(true)
      }
    } catch (error) {
      const authError = error as AuthError
      if (process.env.NODE_ENV === "development") {
        console.error("Auth error:", authError.code, authError.message)
      }
      switch (authError.code) {
        case "auth/invalid-credential":
          setErrorMessage("Felaktig e-postadress eller lösenord. Försök igen.")
          break
        case "auth/email-already-in-use":
          setErrorMessage("E-postadressen används redan. Försök logga in istället.")
          break
        default:
          setErrorMessage("Ett fel uppstod. Försök igen senare.")
      }
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
        <button
          onClick={closeAuthModalAndClearError}
          className="float-right text-gray-500 hover:text-gray-700 bg-white"
        >
          &times;
        </button>
        {verificationSent ? (
          <div>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Verifiera din e-post</h2>
            <p className="text-gray-700">
              Registrering lyckades! En verifieringslänk har skickats till din e-post. Vänligen kontrollera din inkorg
              och klicka på länken för att verifiera ditt konto.
            </p>
            <Button onClick={closeAuthModalAndClearError} className="w-full mt-4">
              Stäng
            </Button>
          </div>
        ) : resetEmailSent ? (
          <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
              <button
                onClick={closeAuthModalAndClearError}
                className="float-right text-gray-500 hover:text-gray-700 bg-white"
              >
                &times;
              </button>
              <div>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">Återställ lösenord</h2>
                <p className="text-gray-700">
                  En länk för att återställa ditt lösenord har skickats till din e-post. Vänligen kontrollera din inkorg
                  och följ instruktionerna för att återställa ditt lösenord.
                </p>
                <Button onClick={closeAuthModalAndClearError} className="w-full mt-4">
                  Stäng
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">{isLogin ? "Logga in" : "Bli medlem"}</h2>
            </div>

            {/* Purchase context explanation - only shown when context is "purchase" */}
            {context === "purchase" && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <h3 className="font-bold text-blue-800 mb-2">Skapa ett konto för att fortsätta</h3>
                <p className="text-sm text-blue-700 mb-2">
                  För att köpa tillgång till skolinformation behöver du först skapa ett konto eller logga in. Fördelarna med ett konto:
                </p>
                <ul className="text-sm text-blue-700 list-disc pl-5 space-y-1">
                  <li>Tillgång till dina köpta skolor från alla enheter</li>
                  <li>Möjlighet att se din köphistorik</li>
                  <li>Samlingsplats för kvitton</li>
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {errorMessage && <div className="text-red-500 mb-4 bg-red-100 p-2 rounded">{errorMessage}</div>}
              <div>
                <Label htmlFor="email" className="text-gray-700">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-gray-700">
                    Password
                  </Label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="text-sm text-blue-600 hover:underline bg-white"
                    >
                      Glömt lösenord?
                    </button>
                  )}
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>
              {!isLogin && (
                <>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="terms" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
                    <Label htmlFor="terms" className="text-sm font-medium leading-none text-gray-700 cursor-pointer">
                      Jag accepterar{" "}
                      <a href="/villkor" target="_blank" className="text-blue-600 hover:underline" rel="noreferrer">
                        användarvillkoren
                      </a>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="newsletter"
                      checked={newsletterOptIn}
                      onChange={(e) => setNewsletterOptIn(e.target.checked)}
                    />
                    <Label
                      htmlFor="newsletter"
                      className="text-sm font-medium leading-none text-gray-700 cursor-pointer"
                    >
                      Jag vill få nyhetsbrev om nya artiklar
                    </Label>
                  </div>
                </>
              )}
              <Button type="submit" className="w-full bg-[#2d4191] hover:bg-[#1e2d6a] text-white">
                {isLogin ? "Logga in" : "Bli medlem"}
              </Button>
            </form>
            <p className="mt-4 text-center text-gray-700">
              {isLogin ? (
                <>
                  Har du inget konto?{" "}
                  <button
                    onClick={() => {
                      setIsLogin(false)
                      setErrorMessage("")
                    }}
                    className="text-blue-600 hover:underline bg-white"
                  >
                    Bli medlem kostnadsfritt
                  </button>
                </>
              ) : (
                <>
                  Har du redan ett konto?{" "}
                  <button
                    onClick={() => {
                      setIsLogin(true)
                      setErrorMessage("")
                    }}
                    className="text-blue-600 hover:underline bg-white"
                  >
                    Logga in
                  </button>
                </>
              )}
            </p>
          </>
        )}
      </div>
    </div>
  )
}

